import React, { useState, useMemo } from 'react';
import { TIFFIN_RECIPES, TIFFIN_PACKING_TIPS } from '../tiffin-data';
import { getRecipeImage } from '../recipe-images';

export default function TiffinBoxChef({ t, lang, onOpenRecipeModal }) {
  const [activeAudience, setActiveAudience] = useState('all'); // 'all', 'school', 'college'
  const [activeDiet, setActiveDiet] = useState('all'); // 'all', 'Vegetarian', 'Non-Vegetarian', 'quick'
  const [expandedTip, setExpandedTip] = useState(null);
  const [expandedSteps, setExpandedSteps] = useState({});

  const isTa = lang === 'Tamil';
  const isHi = lang === 'Hindi';
  const isTe = lang === 'Telugu';

  // Filtered tiffin list
  const filteredTiffins = useMemo(() => {
    return TIFFIN_RECIPES.filter(item => {
      // Filter by Audience
      if (activeAudience !== 'all' && item.audience !== activeAudience) {
        return false;
      }
      // Filter by Diet / Time
      if (activeDiet === 'Vegetarian' && item.diet !== 'Vegetarian') {
        return false;
      }
      if (activeDiet === 'Non-Vegetarian' && item.diet !== 'Non-Vegetarian') {
        return false;
      }
      if (activeDiet === 'quick' && parseInt(item.prepTime) > 15) {
        return false;
      }
      return true;
    });
  }, [activeAudience, activeDiet]);

  const toggleSteps = (id) => {
    setExpandedSteps(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const handleOpenRecipe = async (item) => {
    if (!onOpenRecipeModal) return;
    try {
      const res = await fetch(`/api/recipes?q=`);
      const data = await res.json();
      const allRecipes = data.recipes || [];
      const match = allRecipes.find(r => r.id === item.recipeId) ||
                    allRecipes.find(r => r.core_dish === item.coreDish);

      if (match) {
        const variants = allRecipes.filter(r => r.core_dish === match.core_dish);
        onOpenRecipeModal(match, 4, variants);
      } else {
        // Fallback recipe object
        onOpenRecipeModal({
          id: item.recipeId,
          meal: item.title,
          core_dish: item.coreDish,
          minutes: parseInt(item.prepTime),
          diet: item.diet,
          image: item.image
        }, 4, []);
      }
    } catch (e) {
      console.error('Error opening tiffin recipe modal:', e);
    }
  };

  return (
    <section id="tiffinSection" className="section tiffin-section">
      <div className="container">
        {/* Section Header */}
        <div className="section-header">
          <span className="section-tag">
            {isTa ? '🎒 குழந்தைகள் & மாணவர்கள் லஞ்ச் பாக்ஸ்' : (isHi ? '🎒 बच्चों और छात्रों का टिफिन बॉक्स' : '🎒 KIDS & STUDENTS LUNCHBOX')}
          </span>
          <h2 className="section-title">
            {isTa ? 'ஸ்மார்ட் டிபன் பாக்ஸ் ரெசிபிகள்' : (isHi ? 'स्मार्ट टिफिन बॉक्स रेसिपीज़' : 'Smart Tiffin Box Chef')}
          </h2>
          <p className="section-desc">
            {isTa
              ? 'காலை அவசரத்திலும் 15 நிமிடத்தில் செய்து, மதியம் வரை பஞ்சு போல் மிருதுவாகவும் கெட்டுப்போகாமலும் இருக்கும் ஆரோக்கியமான லஞ்ச் பாக்ஸ் உணவுகள்!'
              : (isHi
                ? 'सुबह की भागदौड़ में 15 मिनट में तैयार, दोपहर तक नरम और ताज़ा रहने वाले पौष्टिक टिफिन बॉक्स!'
                : 'Fast morning prep, balanced pack-and-go lunchbox meals that stay soft, fresh, and delicious for 5 to 8 hours!')}
          </p>
        </div>

        {/* 5 Golden Packing Secrets Strip */}
        <div className="tiffin-tips-banner">
          <div className="tiffin-tips-header">
            <span className="tips-banner-icon">💡</span>
            <h4>
              {isTa ? 'டிபன் பாக்ஸ் பேக்கிங் ரகசியங்கள் (Packing Secrets)' : (isHi ? 'टिफिन पैकिंग के 5 सुनहरे नियम' : '5 Golden Lunchbox Packing Secrets')}
            </h4>
            <span className="tips-badge">
              {isTa ? 'முக்கிய குறிப்புகள்' : (isHi ? 'ज़रूरी टिप्स' : 'Must-Know Hacks')}
            </span>
          </div>
          <div className="tiffin-tips-grid">
            {TIFFIN_PACKING_TIPS.map((tip, idx) => {
              const isSelected = expandedTip === idx;
              return (
                <div
                  key={idx}
                  className={`tiffin-tip-card ${isSelected ? 'active' : ''}`}
                  onClick={() => setExpandedTip(isSelected ? null : idx)}
                >
                  <div className="tip-card-top">
                    <span className="tip-icon">{tip.icon}</span>
                    <h5>{isTa ? tip.title_ta : (isHi ? tip.title_hi : (isTe ? tip.title_te : tip.title))}</h5>
                  </div>
                  <p className="tip-desc">
                    {isTa ? tip.desc_ta : (isHi ? tip.desc : tip.desc)}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Interactive Audience Tabs & Diet Filters */}
        <div className="tiffin-filter-wrapper">
          <div className="tiffin-audience-tabs">
            <button
              type="button"
              className={`audience-tab-btn ${activeAudience === 'all' ? 'active' : ''}`}
              onClick={() => setActiveAudience('all')}
            >
              🍱 {isTa ? 'அனைத்து டிபன்கள்' : (isHi ? 'सभी टिफिन' : 'All Tiffins')} ({TIFFIN_RECIPES.length})
            </button>
            <button
              type="button"
              className={`audience-tab-btn ${activeAudience === 'school' ? 'active' : ''}`}
              onClick={() => setActiveAudience('school')}
            >
              🎒 {isTa ? 'பள்ளி குழந்தைகள் (4-12 வயது)' : (isHi ? 'स्कूल के बच्चे (4-12 वर्ष)' : 'School Kids (4-12 Yrs)')}
            </button>
            <button
              type="button"
              className={`audience-tab-btn ${activeAudience === 'college' ? 'active' : ''}`}
              onClick={() => setActiveAudience('college')}
            >
              🎓 {isTa ? 'கல்லூரி & ஹாஸ்டல் மாணவர்கள்' : (isHi ? 'कॉलेज और हॉस्टल छात्र' : 'College & Hostel')}
            </button>
          </div>

          <div className="tiffin-quick-filters">
            <button
              type="button"
              className={`filter-pill ${activeDiet === 'all' ? 'active' : ''}`}
              onClick={() => setActiveDiet('all')}
            >
              {isTa ? 'அனைத்தும்' : (isHi ? 'सभी' : 'All')}
            </button>
            <button
              type="button"
              className={`filter-pill ${activeDiet === 'Vegetarian' ? 'active' : ''}`}
              onClick={() => setActiveDiet('Vegetarian')}
            >
              🌿 {isTa ? 'சைவம்' : (isHi ? 'शाकाहारी' : 'Pure Veg')}
            </button>
            <button
              type="button"
              className={`filter-pill ${activeDiet === 'Non-Vegetarian' ? 'active' : ''}`}
              onClick={() => setActiveDiet('Non-Vegetarian')}
            >
              🍗 {isTa ? 'முட்டை / அசைவம்' : (isHi ? 'अंडा / नॉन-वेज' : 'Egg / Non-Veg')}
            </button>
            <button
              type="button"
              className={`filter-pill ${activeDiet === 'quick' ? 'active' : ''}`}
              onClick={() => setActiveDiet('quick')}
            >
              ⚡ {isTa ? '15 நிமிட அவசர ரெசிபி' : (isHi ? '15 मिनट क्विक' : '15-Min Rush')}
            </button>
          </div>
        </div>

        {/* Tiffin Recipes Bento Grid */}
        <div className="tiffin-grid">
          {filteredTiffins.map(item => {
            const isStepsOpen = !!expandedSteps[item.id];
            const title = isTa ? item.title_ta : (isHi ? item.title_hi : item.title);
            const audienceLabel = isTa ? item.audienceLabel_ta : item.audienceLabel;
            const stayFresh = isTa ? item.stayFresh_ta : item.stayFresh;
            const badge = isTa ? item.badge_ta : item.badge;
            const mainItem = isTa ? item.mainItem_ta : item.mainItem;
            const side1 = isTa ? item.side1_ta : item.side1;
            const side2 = isTa ? item.side2_ta : item.side2;
            const packTip = isTa ? item.packTip_ta : item.packTip;

            return (
              <div key={item.id} className="tiffin-card">
                {/* Visual Header */}
                <div
                  className="tiffin-card-media"
                  onClick={() => handleOpenRecipe(item)}
                  style={{ cursor: 'pointer' }}
                  title={`${title} - ${isTa ? 'முழு ரெசிபி பார்க்க கிளிக் செய்க' : 'Click to view full recipe'}`}
                >
                  <img
                    src={item.image}
                    alt={title}
                    className="tiffin-img"
                    loading="lazy"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = '/images/recipes/recipe_1.jpg';
                    }}
                  />
                  <div className="tiffin-media-overlay"></div>

                  {/* Top Badges */}
                  <div className="tiffin-badge-row">
                    <span className="tiffin-prep-badge">⏱ {item.prepTime}</span>
                    <span className="tiffin-fresh-badge">❄️ {stayFresh}</span>
                  </div>

                  <span className="tiffin-audience-pill">{audienceLabel}</span>
                  <span className="tiffin-diet-tag">
                    {item.diet === 'Vegetarian' ? '🌿 Veg' : '🍗 Non-Veg'}
                  </span>
                </div>

                {/* Card Content */}
                <div className="tiffin-card-body">
                  <div className="tiffin-title-row">
                    <h3 className="tiffin-title" onClick={() => handleOpenRecipe(item)} style={{ cursor: 'pointer' }}>
                      {title}
                    </h3>
                    <div className="tiffin-rating">
                      ⭐ {item.rating}
                    </div>
                  </div>

                  <div className="tiffin-highlight-badge">
                    ⚡ {badge}
                  </div>

                  {/* Bento Box 3-Compartment Breakdown */}
                  <div className="tiffin-bento-box">
                    <div className="bento-compartment main-comp">
                      <div className="comp-label">
                        <span className="comp-icon">🍱</span>
                        <strong>{isTa ? 'பிரதான உணவு (Main):' : 'Main Dish:'}</strong>
                      </div>
                      <p className="comp-desc">{mainItem}</p>
                    </div>

                    <div className="bento-split-row">
                      <div className="bento-compartment side-comp">
                        <div className="comp-label">
                          <span className="comp-icon">🍎</span>
                          <strong>{isTa ? 'பழம் / சாலட்:' : 'Fruit / Salad:'}</strong>
                        </div>
                        <p className="comp-desc">{side1}</p>
                      </div>

                      <div className="bento-compartment side-comp">
                        <div className="comp-label">
                          <span className="comp-icon">🥜</span>
                          <strong>{isTa ? 'மொறுமொறு சைட்:' : 'Crunch / Side:'}</strong>
                        </div>
                        <p className="comp-desc">{side2}</p>
                      </div>
                    </div>
                  </div>

                  {/* Packing Secret Pill Box */}
                  <div className="tiffin-secret-box">
                    <div className="secret-title">
                      <span>💡</span>
                      <strong>{isTa ? 'பேக்கிங் சீக்ரெட்:' : 'Packing Secret:'}</strong>
                    </div>
                    <p className="secret-desc">{packTip}</p>
                  </div>

                  {/* Expandable Quick 3-Step Morning Routine */}
                  {isStepsOpen && (
                    <div className="tiffin-quick-steps-box animate-fadeIn">
                      <h5>{isTa ? '🌅 3-படி காலை அவசர முறை:' : '🌅 3-Step Morning Shortcut:'}</h5>
                      <ol className="tiffin-steps-list">
                        {item.quickSteps.map((s, i) => (
                          <li key={i}>
                            <span className="step-num">{s.step}</span>
                            <span className="step-txt">{s.text}</span>
                          </li>
                        ))}
                      </ol>
                    </div>
                  )}

                  {/* Actions Row */}
                  <div className="tiffin-actions-row">
                    <button
                      type="button"
                      className="btn btn-primary btn-sm tiffin-cook-btn"
                      onClick={() => handleOpenRecipe(item)}
                    >
                      📖 {isTa ? 'ரெசிபி & அளவுகள்' : (isHi ? 'रेसिपी और सामग्री' : 'Recipe & Quantities')}
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline btn-sm tiffin-steps-btn"
                      onClick={() => toggleSteps(item.id)}
                    >
                      {isStepsOpen
                        ? (isTa ? '▲ மறை' : '▲ Hide Steps')
                        : (isTa ? '▼ செய்முறை' : '▼ 3-Step Prep')}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

